import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";

import { getBreadcrumbs, getCategoryProducts } from "../../actions/categoriesActions";
import { getPopularProducts, getStorages, makeRequest } from "../../actions/productsActions";
import { sortProductsByWarehouse } from "../../helpers/functions";


import TransitionedBlock from "../HelperComponents/TransitionedBlock/TransitionedBlock";
import StockBlock from "./StockBlock";
import ShopPageCard from "./ShopPageCard";
import FilterComponent from "../HelperComponents/FilterComponent";
import SortComponent from "../HelperComponents/SortComponent";
import Pagination from "../HelperComponents/Pagination/Pagination";


import "./ShopPage.scss";

const PAGE_SIZE = 8;

const ShopPage = ({ history, location }) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(true);
    const activePage = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return parseInt(params.get("page")) || 1;
    }, [location.search]);
    const filter = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const f = params.get("filter");
        return f ? f.split(",") : [];
    }, [location.search]);

    const sort = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get("ordering") || "availability";
    }, [location.search]);



    const { products, breadcrumbs } = useSelector(state => ({
        products: {
            popular: state.products.popular_products,
            category: state.category.products_results
        },
        breadcrumbs: state.category.breadcrumbs,
        specialProducts: state.products.special_products
    }));

    const categorySlug = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get("cat");
    }, [location.search]);

    useEffect(() => {
        setLoading(true);

        if (categorySlug) {
            const queryParams = new URLSearchParams({
                page: activePage,
                page_size: PAGE_SIZE,
            });

            if (filter.length > 0) {
                queryParams.set('filter', filter.join(","));
            }
            if (sort) {

                queryParams.set('ordering', sort);
            }

            const queryString = `?${queryParams.toString()}`;

            dispatch(getCategoryProducts(categorySlug, queryString))
                .finally(() => setLoading(false));

            dispatch(getBreadcrumbs("3093"));
        } else {
            Promise.all([
                dispatch(getPopularProducts(filter, activePage, PAGE_SIZE)),

            ]).finally(() => setLoading(false));
        }
    }, [categorySlug, activePage, filter, sort, dispatch]);




    const handlePageChange = useCallback((page) => {
        window.scrollTo(0, 0);
    }, []);

    const goToProd = useCallback((slug) => {
        history.push(`/main/shop/product/${slug}`);
    }, [history]);

    const handleFilter = useCallback((filters) => {
        if (JSON.stringify(filters) === JSON.stringify(filter)) return;
        const params = new URLSearchParams(location.search);
        params.set("page", 1);
        if (filters.length > 0) {
            params.set("filter", filters.join(","));
        } else {
            params.delete("filter");
        }
        history.push({ search: params.toString() });
    }, [history, location.search, filter]);

    const handleSort = useCallback((value) => {
        if (value === sort) return;
        const params = new URLSearchParams(location.search);
        params.set("page", 1);
        if (value) {
            params.set("ordering", value);
        } else {
            params.delete("ordering");
        }
        history.push({ search: params.toString() });
    }, [history, location.search, sort]);



    const isCategoryPage = !!categorySlug;
    const productData = isCategoryPage ? products.category : products.popular;

    const sortedProductResults = useMemo(() => {
        return sortProductsByWarehouse(productData?.results);
    }, [productData?.results]);

    const lastBreadcrumb = breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1] : null;
    const pageTitle = isCategoryPage
        ? (lastBreadcrumb && lastBreadcrumb.name ? lastBreadcrumb.name : "Загрузка...")
        : "Популярные товары";

    return (
        <TransitionedBlock classes="transitioned_block">
            <div className="page_wrapper shop_page">
                <div className="shop_content">
                    {!isCategoryPage && (
                        <StockBlock
                            filter={filter}
                            loading={loading}
                            getStorages={(...args) => dispatch(getStorages(...args))}
                            makeRequest={(...args) => dispatch(makeRequest(...args))}
                            goToProd={goToProd}
                            handleFilter={handleFilter}
                        />
                    )}
                    <header className="content_header">
                        <div className="title_page">{pageTitle}</div>
                        {isCategoryPage && (
                            <>
                                <FilterComponent handleFilter={handleFilter} reset={pageTitle} initialSelect={filter} />
                                <SortComponent handleSort={handleSort} reset={pageTitle} initialValue={sort} />

                            </>
                        )}
                    </header>
                    <div className="catalog_product">
                        {loading ? (
                            <p>Идет загрузка товара...</p>
                        ) : (
                            <>
                                {sortedProductResults && sortedProductResults.length > 0 ? (
                                    sortedProductResults.map((el, idx) => {
                                        let sum = 0;
                                        if (el.storage_quantity) {
                                            el.storage_quantity.forEach(item => {
                                                sum += item.quantity ? item.quantity : 0;
                                            });
                                        }
                                        return (
                                            <ShopPageCard
                                                key={el.id || idx}
                                                el={el}
                                                idx={idx}
                                                sum={sum}
                                                goToProd={goToProd}
                                                getStorages={(...args) => dispatch(getStorages(...args))}
                                                productsStoragesName={isCategoryPage ? "products_results" : "popular_products"}
                                                makeRequest={(...args) => dispatch(makeRequest(...args))}
                                            />
                                        );
                                    })
                                ) : (
                                    <p>Нет товаров</p>
                                )}
                            </>
                        )}
                    </div>
                    {!loading && productData && productData.count > PAGE_SIZE && (
                        <Pagination
                            pageCount={Math.ceil(productData.count / PAGE_SIZE)}
                            onPageChange={handlePageChange}
                            forcePage={activePage - 1}
                        />
                    )}
                </div>
            </div>
        </TransitionedBlock>
    );
};

export default withRouter(ShopPage);